// Copyright (c) Ping. All rights reserved.

/**
 * Threads module — cross-platform types.
 *
 * Sensitive fields (treat as items from the `safe-logging` never-log
 * table): `previewText`, `body`, `displayName`, `authorName`, `imageUri`.
 * Never pass these to any logger, Sentry breadcrumb, or analytics event.
 */

// Brand types live in `util/ids` so the barrel doesn't fan-out
// duplicate `export *` re-exports. Imported (not re-exported) here.
import type { MessageId, UserId } from '../util/ids';

/**
 * Opaque thread identifier. The host decides the actual shape (today it
 * is the parent message id; tomorrow it may be a server-issued id). The
 * slice treats it as a string.
 */
export type ThreadIdentifier = string & { readonly __brand: 'ThreadIdentifier' };

/** Lifecycle of a single reply row, mirroring `MessageState` semantics. */
export type ThreadReplyStatus = 'sending' | 'sent' | 'delivered' | 'read' | 'failed';

/** Parent-message preview shown above the reply timeline. */
export interface ThreadParentPreview {
  readonly messageId: MessageId;
  readonly authorId: UserId;
  readonly authorName: string;
  /**
   * Resolved avatar URL of the parent author, or null to fall back to
   * initials. Sensitive — never log. Populated by the host gateway the same
   * way a reply's `authorImageUri` is, so the thread header shows the real
   * picture instead of a bare initial.
   */
  readonly authorImageUri?: string | null | undefined;
  /** Sensitive — never log. */
  readonly previewText: string;
  readonly timestamp: number;
  readonly deleted: boolean;
}

/** Recent participant in the thread (used by `ThreadParticipantStrip`). */
export interface ThreadParticipant {
  readonly userId: UserId;
  readonly displayName: string;
  readonly imageUri: string | null;
  readonly lastActiveTimestamp: number;
}

/** A single reply row in the thread timeline. */
export interface ThreadReply {
  readonly id: MessageId;
  readonly threadId: ThreadIdentifier;
  readonly authorId: UserId;
  readonly authorName: string;
  readonly authorImageUri: string | null;
  /** Sensitive — never log. Human-readable preview of the reply body. */
  readonly body: string;
  /**
   * Raw envelope JSON of the reply, when available. The thread row
   * derives a media variant from it (image / voice / poll / file / …)
   * so media replies render as proper tiles, not just a text preview.
   * Sensitive — never log.
   */
  readonly rawBody?: string | undefined;
  readonly timestamp: number;
  readonly isSelf: boolean;
  readonly status: ThreadReplyStatus;
  /**
   * Peer read receipts on THIS reply (parity with the main chat's
   * `Message.readBy`). Same shape the read-side `Message` uses so the
   * panel can assign it straight through to `MessageRow`, which flips a
   * self reply's tick from single- to double-check once non-empty. The
   * host projects it from the persisted row (deduped one entry per
   * account). Empty/omitted → single-check (delivered) or plain sent.
   */
  readonly readBy?: ReadonlyArray<{ userId: string; timestamp: number }> | undefined;
  /**
   * Wall-clock ms of the reply's latest edit (`com.ping.edit`). Drives
   * the "(edited)" affordance on the thread tile — parity with the main
   * chat. Undefined = never edited.
   */
  readonly editedAt?: number | undefined;
}

/**
 * Summary record consumed by the in-chat affordance. Derived by the host
 * from gateway data; the slice only renders it.
 */
export interface ThreadSummary {
  readonly identifier: ThreadIdentifier;
  readonly replyCount: number;
  readonly unreadCount: number;
  readonly recentParticipants: readonly ThreadParticipant[];
  readonly lastReplyTimestamp: number | null;
  readonly closedByAuthor: boolean;
}

/**
 * View-model state machine. `loading` and `error` carry their identifier
 * so the host can render a stable header even while the timeline is not
 * yet available.
 */
export type ThreadState =
  | { readonly kind: 'closed' }
  | { readonly kind: 'loading'; readonly identifier: ThreadIdentifier }
  | {
      readonly kind: 'open';
      readonly identifier: ThreadIdentifier;
      readonly parent: ThreadParentPreview;
      readonly replies: readonly ThreadReply[];
      readonly participants: readonly ThreadParticipant[];
      readonly sending: boolean;
    }
  | {
      readonly kind: 'error';
      readonly identifier: ThreadIdentifier;
      readonly error: Error;
    };

/** Snapshot pushed by `ThreadGateway.subscribe` and returned by `load`. */
export interface ThreadOpenSnapshot {
  readonly parent: ThreadParentPreview;
  readonly replies: readonly ThreadReply[];
  readonly participants: readonly ThreadParticipant[];
}

/**
 * Host-provided async port. The slice never imports the SDK directly —
 * the host wires this gateway up against `MessagingClient` (or a mock
 * in tests). All methods are idempotent.
 */
export interface ThreadGateway {
  load(identifier: ThreadIdentifier): Promise<ThreadOpenSnapshot>;
  subscribe(
    identifier: ThreadIdentifier,
    callback: (snapshot: ThreadOpenSnapshot) => void,
  ): () => void;
  sendReply(identifier: ThreadIdentifier, body: string): Promise<void>;
  markAsRead(identifier: ThreadIdentifier): Promise<void>;
}

/** Visible-avatar cap for `ThreadParticipantStrip`. */
export const THREAD_PARTICIPANT_STRIP_VISIBLE_LIMIT = 5;
